import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { beginLogin, completeLoginFromUrl, hasAllScopes, refreshTokens } from './pkce.ts';
import {
  clearTokens,
  isExpired,
  loadClientId,
  loadTokens,
  saveClientId,
  saveTokens,
  type StoredTokens,
} from './tokens.ts';

export type AuthStatus =
  | 'initializing'
  | 'needsClientId'
  | 'loggedOut'
  | 'loggedIn';

interface AuthContextValue {
  status: AuthStatus;
  clientId: string;
  error: string | null;
  setClientId: (value: string) => void;
  login: () => Promise<void>;
  logout: () => void;
  clearError: () => void;
  /** Liefert ein gueltiges Access-Token, erneuert es bei Bedarf. */
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface BootstrapResult {
  tokens: StoredTokens | null;
  error: string | null;
}

let bootstrapPromise: Promise<BootstrapResult> | null = null;

/**
 * Verarbeitet den Auth-Callback bzw. laedt gespeicherte Tokens.
 *
 * Bewusst ausserhalb des React-Lebenszyklus: ein Authorization Code ist
 * einmalig einloesbar. Wuerde das im Effekt passieren, wuerde StrictMode den
 * Effekt doppelt ausfuehren und der erste (abgebrochene) Durchlauf haette den
 * Code bereits verbraucht. Das Ergebnis wird hier zusaetzlich sofort
 * persistiert, damit ein Abbruch der Komponente die Tokens nicht verwirft.
 */
function bootstrapAuth(): Promise<BootstrapResult> {
  bootstrapPromise ??= (async (): Promise<BootstrapResult> => {
    const clientId = loadClientId();
    if (!clientId) return { tokens: null, error: null };

    try {
      const fromCallback = await completeLoginFromUrl(clientId);
      if (fromCallback) {
        saveTokens(fromCallback);
        return { tokens: fromCallback, error: null };
      }
    } catch (err) {
      return { tokens: null, error: err instanceof Error ? err.message : String(err) };
    }

    const stored = loadTokens();
    if (stored && !hasAllScopes(stored.scope)) {
      // Die App braucht inzwischen mehr Rechte als beim letzten Login erteilt.
      clearTokens();
      return {
        tokens: null,
        error: 'Die App benötigt zusätzliche Spotify-Berechtigungen. Bitte erneut anmelden.',
      };
    }

    return { tokens: stored, error: null };
  })();

  return bootstrapPromise;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [clientId, setClientIdState] = useState<string>(() => loadClientId());
  const [error, setError] = useState<string | null>(null);

  const tokensRef = useRef<StoredTokens | null>(null);
  const refreshPromiseRef = useRef<Promise<StoredTokens> | null>(null);
  const clientIdRef = useRef(clientId);
  clientIdRef.current = clientId;

  const applyTokens = useCallback((tokens: StoredTokens | null) => {
    tokensRef.current = tokens;
    if (tokens) {
      saveTokens(tokens);
      setStatus('loggedIn');
    } else {
      clearTokens();
      setStatus(clientIdRef.current ? 'loggedOut' : 'needsClientId');
    }
  }, []);

  // Beim Start: Callback verarbeiten oder gespeicherte Tokens laden.
  useEffect(() => {
    let cancelled = false;

    void bootstrapAuth().then(({ tokens, error: bootstrapError }) => {
      if (cancelled) return;
      if (bootstrapError) setError(bootstrapError);

      if (tokens) {
        tokensRef.current = tokens;
        setStatus('loggedIn');
      } else {
        setStatus(loadClientId() ? 'loggedOut' : 'needsClientId');
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const getAccessToken = useCallback(async (): Promise<string> => {
    const current = tokensRef.current;
    if (!current) throw new Error('Nicht bei Spotify angemeldet.');
    if (!isExpired(current)) return current.accessToken;

    // Single-Flight: parallele Aufrufe teilen sich denselben Refresh.
    refreshPromiseRef.current ??= refreshTokens(clientIdRef.current, current.refreshToken)
      .then((fresh) => {
        applyTokens(fresh);
        return fresh;
      })
      .catch((err: unknown) => {
        applyTokens(null);
        throw err instanceof Error ? err : new Error(String(err));
      })
      .finally(() => {
        refreshPromiseRef.current = null;
      });

    const refreshed = await refreshPromiseRef.current;
    return refreshed.accessToken;
  }, [applyTokens]);

  const setClientId = useCallback((value: string) => {
    const trimmed = value.trim();
    saveClientId(trimmed);
    setClientIdState(trimmed);
    setStatus((prev) =>
      prev === 'loggedIn' ? prev : trimmed ? 'loggedOut' : 'needsClientId',
    );
  }, []);

  const login = useCallback(async () => {
    setError(null);
    if (!clientIdRef.current) {
      setError('Bitte zuerst die Spotify Client ID eintragen.');
      return;
    }
    await beginLogin(clientIdRef.current);
  }, []);

  const logout = useCallback(() => {
    applyTokens(null);
  }, [applyTokens]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      clientId,
      error,
      setClientId,
      login,
      logout,
      clearError: () => setError(null),
      getAccessToken,
    }),
    [status, clientId, error, setClientId, login, logout, getAccessToken],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von <AuthProvider> genutzt werden.');
  return ctx;
}
